// src/components/Profiles/Profiles.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import DigitalCard from '../DigitalCard/DigitalCard';
import styles from './Profiles.module.css';

const Profiles = () => {
  const navigate = useNavigate();
  const [featuredProfiles, setFeaturedProfiles] = useState([]);
  const [trendingProfiles, setTrendingProfiles] = useState([]);
  const [newProfiles, setNewProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('featured');

  useEffect(() => {
    const fetchProfiles = async () => {
      setIsLoading(true);
      try {
        // Get all users - simplified approach that works with your data
        const usersSnap = await getDocs(collection(db, 'users'));
        
        const allUsers = usersSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(user => user.publicProfile === true); // Only show public profiles

        console.log('Found public profiles:', allUsers.length);
        
        // Organize profiles by different criteria using available data
        const featured = allUsers.filter(user => 
          user.photos?.length > 0 || user.videos?.length > 0 || user.bio
        ).slice(0, 6);
        
        const trending = allUsers.filter(user => 
          user.displayName && user.displayName !== ''
        ).slice(0, 6);
        
        const recent = allUsers.slice(0, 6); // Just show all for now

        setFeaturedProfiles(featured);
        setTrendingProfiles(trending);
        setNewProfiles(recent);
        
      } catch (error) {
        console.error('Error fetching profiles:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfiles();
  }, []);

  const getCurrentProfiles = () => {
    switch (activeCategory) {
      case 'featured': return featuredProfiles;
      case 'trending': return trendingProfiles;
      case 'new': return newProfiles;
      default: return [];
    }
  };

  const profiles = getCurrentProfiles();

  // Fix navigation mismatch - use proper username for navigation
  const getProfileUrl = (profile) => {
    // If profile has a username-style ID, use it
    if (profile.id.includes('.') || profile.id.includes('@')) {
      return `/profiles/${profile.id}`;
    }
    // If it's a UID, try to use email prefix
    if (profile.email) {
      return `/profiles/${profile.email.split('@')[0]}`;
    }
    // Fallback to ID
    return `/profiles/${profile.id}`;
  };

  return (
    <main className={styles.profilesPage}>
      <section className={styles.profilesHeader}>
        <h1>Profiles</h1>
        <p className={styles.profilesSubtitle}>
          Discover creative individuals in the Artifact community
        </p>
      </section>

      <section className={styles.profilesCategories}>
        {['featured', 'trending', 'new'].map(cat => (
          <button
            key={cat}
            className={`${styles.categoryButton} ${activeCategory === cat ? styles.active : ''}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </button>
        ))}
      </section>

      {isLoading ? (
        <div className={styles.loadingSpinner}>Loading profiles...</div>
      ) : (
        <section className={styles.profilesGrid}>
          {profiles.length > 0 ? (
            profiles.map(profile => (
              <div
                key={profile.id}
                className={styles.profileCard}
                onClick={() => navigate(getProfileUrl(profile))}
              >
                <DigitalCard 
                  userData={profile} 
                  isPrivateView={false}
                  linkToProfile={false}
                />
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>
              <p>No public profiles found. Be the first to create one!</p>
            </div>
          )}
        </section>
      )}

      <section className={styles.createProfileCta}>
        <h2>Join the Community</h2>
        <p>Create your own Digital Card and share your creative journey</p>
        <button 
          className={styles.ctaButton}
          onClick={() => navigate('/card-generator')}
        >
          Create Your Digital Card
        </button>
      </section>

      <section className={styles.profilesInfo}>
        <div className={styles.infoCard}>
          <h3>Dynamic Identity</h3>
          <p>Your Digital Card evolves with your creative journey.</p>
        </div>
        <div className={styles.infoCard}>
          <h3>Curated Discovery</h3>
          <p>Exceptional content from profiles may appear across Artifact.</p>
        </div>
        <div className={styles.infoCard}>
          <h3>Connected Experience</h3>
          <p>Your profile connects seamlessly with the platform.</p>
        </div>
      </section>
    </main>
  );
};

export default Profiles;